"use client";

import React from "react";
import { ArrowRight, ArrowLeft, Minus } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const caseStudies = [{
  id: 1,
  category: "Real Estate",
  challenge: "Complex Liquidity Event",
  challengeDesc: "A client faced significant tax exposure while liquidating a $50M commercial real estate portfolio across three states.",
  outcome: "Optimized Wealth Transfer",
  outcomeDesc: "We structured a 1031 exchange combined with a charitable remainder trust, preserving 28% more capital for heirs."
}, {
  id: 2,
  category: "Business Succession",
  challenge: "Multi-Generational Transition",
  challengeDesc: "A family-owned manufacturing firm needed a succession plan that balanced liquidity for retiring founders with control for the next generation.",
  outcome: "Legacy Preservation",
  outcomeDesc: "Implemented a dual-class stock structure and an intentionally defective grantor trust to seamless transfer leadership and value."
}, {
  id: 3,
  category: "High Net Worth",
  challenge: "Concentrated Stock Risk",
  challengeDesc: "An executive held 85% of their net worth in a single volatile tech stock, creating unacceptable downside risk.",
  outcome: "Risk-Adjusted Growth",
  outcomeDesc: "Utilized an exchange fund and zero-cost collar strategy to diversify exposure without triggering immediate capital gains taxes."
}, {
  id: 4,
  category: "Global Wealth",
  challenge: "Cross-Border Complexity",
  challengeDesc: "A dual-citizen family required a unified strategy to manage assets and tax liabilities in both the US and Europe.",
  outcome: "Unified Global Strategy",
  outcomeDesc: "Harmonized investment accounts and estate plans to ensure tax efficiency and compliance in both jurisdictions."
}, {
  id: 5,
  category: "Philanthropy",
  challenge: "Charitable Impact Scaling",
  challengeDesc: "A retired entrepreneur wanted to maximize the impact of their foundation while maintaining family involvement.",
  outcome: "Enduring Social Capital",
  outcomeDesc: "Established a donor-advised fund structure that engaged multiple generations in philanthropic decision-making."
}];
export default function SuccessBlueprints() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="success-blueprints" ref={ref} className={`dark bg-background text-foreground py-24 relative overflow-hidden transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      {}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />

      <div className="container mx-auto px-6 md:px-12 relative z-10">
        <div className="mb-16 max-w-3xl">
          <h2 className="text-primary font-medium tracking-widest uppercase text-sm mb-4">
            Case Studies
          </h2>
          <h3 className="text-4xl md:text-5xl font-serif text-foreground mb-6">
            Success Blueprints
          </h3>
          <p className="text-muted-foreground text-lg font-light max-w-2xl">
            Navigating complexity with precision. Explore how we transform financial challenges into enduring legacies through strategic foresight.
          </p>
        </div>

        <Carousel opts={{
        align: "start",
        loop: true
      }} className="w-full pb-24">
          <CarouselContent className="-ml-4 md:-ml-8">
            {caseStudies.map((study, i) => <CarouselItem key={study.id} data-index={i} className="pl-4 md:pl-8 md:basis-1/2 lg:basis-1/3">
                <div className="group h-full flex flex-col bg-card/5 border border-white/5 hover:border-primary/30 transition-colors duration-500 rounded-sm overflow-hidden">
                  {}
                  <div className="p-8 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 mb-4">
                      <span className="w-2 h-2 rounded-full bg-primary/60" />
                      <span className="text-xs uppercase tracking-wider text-muted-foreground">
                        The Challenge
                      </span>
                    </div>
                    <h4 className="text-xl font-medium text-foreground mb-3 group-hover:text-primary transition-colors duration-300">
                      {study.challenge}
                    </h4>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {study.challengeDesc}
                    </p>
                  </div>

                  {}
                  <div className="px-8">
                    <Separator className="bg-white/10 group-hover:bg-primary/20 transition-colors duration-500" />
                  </div>

                  {}
                  <div className="p-8 flex-1 flex flex-col justify-end bg-gradient-to-b from-transparent to-black/20">
                    <div className="flex items-center gap-2 mb-4">
                      <Minus className="w-4 h-px text-primary" />
                      <span className="text-xs uppercase tracking-wider text-primary">
                        The Strategic Outcome
                      </span>
                    </div>
                    <h4 className="text-2xl md:text-3xl font-serif text-foreground mb-3 italic">
                      {study.outcome}
                    </h4>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                      {study.outcomeDesc}
                    </p>
                    
                    <div className="mt-auto pt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform translate-y-2 group-hover:translate-y-0">
                      <Link to="/WealthArchitecturePage" className="inline-flex items-center text-xs uppercase tracking-widest text-primary hover:text-white transition-colors">
                        View Details <ArrowRight className="ml-2 w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                </div>
              </CarouselItem>)}
          </CarouselContent>

          {}
          <div className="absolute bottom-0 right-0 flex gap-4 pr-4 md:pr-0">
            <CarouselPrevious className="static translate-y-0 h-12 w-12 rounded-none border border-primary/30 bg-transparent text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300" variant="outline" />
            <CarouselNext className="static translate-y-0 h-12 w-12 rounded-none border border-primary/30 bg-transparent text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300" variant="outline" />
          </div>
        </Carousel>
      </div>
    </section>;
}